import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Stethoscope, ArrowLeft, Shield, Lock, Eye, Trash2, FileText, Bell } from 'lucide-react';

const Section = ({ id, icon: Icon, title, children }) => (
  <section id={id} className="scroll-mt-20">
    <div className="flex items-center gap-3 mb-4">
      <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: '#d6e3ff' }}>
        <Icon className="w-4 h-4" style={{ color: '#00478d' }} />
      </div>
      <h2 className="font-bold text-lg" style={{ color: '#191c1e' }}>{title}</h2>
    </div>
    <div className="text-sm leading-relaxed space-y-3 pl-12" style={{ color: '#424752' }}>
      {children}
    </div>
  </section>
);

const PrivacyPage = () => {
  const toc = [
    { id: 'collection', label: 'Information We Collect' },
    { id: 'use', label: 'How We Use Your Data' },
    { id: 'hipaa', label: 'HIPAA Compliance' },
    { id: 'sharing', label: 'Data Sharing' },
    { id: 'rights', label: 'Your Rights' },
    { id: 'cookies', label: 'Cookies & Tracking' },
    { id: 'retention', label: 'Data Retention' },
    { id: 'contact', label: 'Contact Us' },
  ];

  return (
    <div className="min-h-screen font-sans" style={{ background: '#f9f9fd', color: '#191c1e' }}>
      {/* Nav */}
      <nav className="sticky top-0 z-50 bg-white/90 backdrop-blur-xl border-b border-black/5">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg,#00478d,#005eb8)' }}>
              <Stethoscope className="w-3.5 h-3.5 text-white" />
            </div>
            <span className="font-bold text-base tracking-tight" style={{ color: '#191c1e' }}>
              Sympto<span style={{ color: '#00478d' }}>Connect</span>
            </span>
          </Link>
          <Link to="/" className="flex items-center gap-1.5 text-sm font-semibold hover:text-[#00478d] transition-colors" style={{ color: '#424752' }}>
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </Link>
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12 lg:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-10">

          {/* Sidebar TOC */}
          <aside className="hidden lg:block">
            <div className="sticky top-24">
              <p className="text-xs font-bold uppercase tracking-wider mb-4" style={{ color: '#727783' }}>On this page</p>
              <nav className="space-y-1">
                {toc.map(t => (
                  <a
                    key={t.id}
                    href={`#${t.id}`}
                    className="block text-sm py-1.5 px-3 rounded-lg transition-colors hover:bg-white hover:text-[#00478d]"
                    style={{ color: '#424752' }}
                  >
                    {t.label}
                  </a>
                ))}
              </nav>
            </div>
          </aside>

          {/* Content */}
          <main className="lg:col-span-3 space-y-12">
            {/* Header */}
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider mb-5" style={{ background: '#d6e3ff', color: '#00478d' }}>
                <Shield className="w-3.5 h-3.5" /> Privacy Policy
              </div>
              <h1 className="font-extrabold mb-3" style={{ fontSize: 'clamp(1.75rem,4vw,2.5rem)', letterSpacing: '-0.02em', color: '#191c1e' }}>
                Your privacy is our priority
              </h1>
              <p className="text-base leading-relaxed" style={{ color: '#424752' }}>
                SymptoConnect, Inc. ("SymptoConnect," "we," "our," or "us") is committed to protecting your health information. This Privacy Policy explains how we collect, use, share, and safeguard your personal and health data when you use our platform.
              </p>
              <p className="text-xs mt-4 font-semibold" style={{ color: '#727783' }}>Effective date: January 1, 2026 · Last updated: April 11, 2026</p>
            </div>

            <Section id="collection" icon={FileText} title="Information We Collect">
              <p><strong style={{ color: '#191c1e' }}>Account information.</strong> When you register, we collect your name, email address, password hash, phone number, and role (patient or clinician).</p>
              <p><strong style={{ color: '#191c1e' }}>Health data you provide.</strong> Symptom reports, vital readings, medication lists, conditions, allergies, lab results, care transitions, social history, and mental health information you or your care team enter into the platform.</p>
              <p><strong style={{ color: '#191c1e' }}>Communications.</strong> Messages exchanged between you and your care team within the platform's chat and notification system.</p>
              <p><strong style={{ color: '#191c1e' }}>Usage data.</strong> Log data including IP addresses, device type, browser, pages viewed, and session timestamps — used solely to maintain and improve the service.</p>
              <p><strong style={{ color: '#191c1e' }}>Cookies and local storage.</strong> Session tokens and preference data stored locally. See the Cookies section below.</p>
            </Section>

            <Section id="use" icon={Eye} title="How We Use Your Data">
              <p>We use your information to:</p>
              <ul className="list-disc pl-5 space-y-1.5">
                <li>Provide, operate, and improve the SymptoConnect platform</li>
                <li>Power AI-based risk assessments and care recommendations</li>
                <li>Facilitate communication between you and your authorized care team</li>
                <li>Send you notifications about your health status and care actions</li>
                <li>Fulfill our legal obligations under HIPAA and applicable state law</li>
                <li>Detect, investigate, and prevent security incidents or abuse</li>
                <li>Conduct anonymized, aggregate research to improve health outcomes (with your consent)</li>
              </ul>
              <p>We do <strong style={{ color: '#191c1e' }}>not</strong> sell your personal or health data to third parties, use your health data for advertising, or share it with insurers or employers without your explicit written consent.</p>
            </Section>

            <Section id="hipaa" icon={Shield} title="HIPAA Compliance">
              <p>SymptoConnect is a HIPAA-covered entity and treats all patient health information as Protected Health Information (PHI) under the Health Insurance Portability and Accountability Act of 1996.</p>
              <ul className="list-disc pl-5 space-y-1.5">
                <li><strong style={{ color: '#191c1e' }}>Encryption at rest and in transit.</strong> Clinical fields — symptom descriptions, doctor notes, diagnoses, and medications — are encrypted at the field level using AES-256-GCM before being written to the database, so that raw database access cannot reveal patient health information. All data is additionally protected by TLS 1.3 in transit.</li>
                <li><strong style={{ color: '#191c1e' }}>Access controls.</strong> Clinicians can only access patient data with explicit patient consent, except in emergencies where a Treatment Exception applies under HIPAA.</li>
                <li><strong style={{ color: '#191c1e' }}>Audit trails.</strong> All access to PHI is logged, timestamped, and auditable by patients through the Data Transparency dashboard.</li>
                <li><strong style={{ color: '#191c1e' }}>Business Associate Agreements.</strong> All third-party vendors with access to PHI have signed BAAs with SymptoConnect.</li>
                <li><strong style={{ color: '#191c1e' }}>Breach notification.</strong> In the unlikely event of a breach affecting your PHI, we will notify you within 60 days as required by law.</li>
              </ul>
            </Section>

            <Section id="sharing" icon={Bell} title="Data Sharing">
              <p>We share your information only as follows:</p>
              <ul className="list-disc pl-5 space-y-1.5">
                <li><strong style={{ color: '#191c1e' }}>Your care team.</strong> Doctors and specialists you explicitly add receive access scoped by your consent settings. You can revoke access at any time.</li>
                <li><strong style={{ color: '#191c1e' }}>Service providers.</strong> Cloud infrastructure, database, and communication vendors operating under BAAs (e.g. hosting, SMS, email delivery).</li>
                <li><strong style={{ color: '#191c1e' }}>Legal requirements.</strong> When required by court order, subpoena, or applicable law, or to protect the safety of patients or the public.</li>
                <li><strong style={{ color: '#191c1e' }}>Business transfers.</strong> In the event of a merger or acquisition, your data may transfer to the successor entity subject to the same privacy protections.</li>
              </ul>
              <p>We never share your PHI with data brokers, advertisers, insurance companies for underwriting purposes, or employers.</p>
            </Section>

            <Section id="rights" icon={Lock} title="Your Rights">
              <p>Depending on your jurisdiction, you have the right to:</p>
              <ul className="list-disc pl-5 space-y-1.5">
                <li><strong style={{ color: '#191c1e' }}>Access.</strong> Download a copy of all your health data through My Data & Privacy → Export My Data.</li>
                <li><strong style={{ color: '#191c1e' }}>Correction.</strong> Request correction of inaccurate personal information by contacting support.</li>
                <li><strong style={{ color: '#191c1e' }}>Deletion.</strong> Request deletion of your account and associated data via Settings → Account. Medical records required by law will be retained for the minimum legally required period.</li>
                <li><strong style={{ color: '#191c1e' }}>Restrict processing.</strong> Suspend or revoke data sharing with specific providers at any time through consent settings.</li>
                <li><strong style={{ color: '#191c1e' }}>Portability.</strong> Export your data in machine-readable format (JSON) from the platform.</li>
                <li><strong style={{ color: '#191c1e' }}>Opt out of research.</strong> Withdraw consent for anonymized research use at any time in your privacy settings.</li>
              </ul>
              <p>To exercise any of these rights, contact <a href="mailto:privacy@symptoconnect.com" className="underline" style={{ color: '#00478d' }}>privacy@symptoconnect.com</a> or visit the <Link to="/my-data" className="underline" style={{ color: '#00478d' }}>My Data & Privacy</Link> page.</p>
            </Section>

            <Section id="cookies" icon={Eye} title="Cookies & Tracking">
              <p>SymptoConnect uses the following technologies:</p>
              <ul className="list-disc pl-5 space-y-1.5">
                <li><strong style={{ color: '#191c1e' }}>Session cookies.</strong> Strictly necessary to keep you logged in during your session. Cannot be disabled without breaking core functionality.</li>
                <li><strong style={{ color: '#191c1e' }}>Local storage.</strong> Stores UI preferences (theme, notification settings) locally on your device.</li>
                <li><strong style={{ color: '#191c1e' }}>Analytics.</strong> Anonymized, aggregate usage analytics to understand feature adoption and improve the product. No PHI is included. You may opt out in Settings.</li>
              </ul>
              <p>We do not use third-party advertising cookies or tracking pixels. Our analytics provider has signed a BAA and does not re-identify or sell your data.</p>
            </Section>

            <Section id="retention" icon={Trash2} title="Data Retention">
              <p>We retain your data as follows:</p>
              <ul className="list-disc pl-5 space-y-1.5">
                <li><strong style={{ color: '#191c1e' }}>Active accounts.</strong> Data is retained for as long as your account is active.</li>
                <li><strong style={{ color: '#191c1e' }}>After account deletion.</strong> Personal profile data is deleted within 30 days. Health records may be retained for up to 7 years or as required by applicable state medical record law.</li>
                <li><strong style={{ color: '#191c1e' }}>Audit logs.</strong> Access logs are retained for 6 years as required by HIPAA.</li>
                <li><strong style={{ color: '#191c1e' }}>Backups.</strong> Encrypted backups are purged within 90 days of account deletion.</li>
              </ul>
            </Section>

            <Section id="contact" icon={FileText} title="Contact Us">
              <p>For privacy-related questions, requests, or concerns:</p>
              <ul className="list-none space-y-2">
                <li><strong style={{ color: '#191c1e' }}>Privacy Officer:</strong> <a href="mailto:privacy@symptoconnect.com" className="underline" style={{ color: '#00478d' }}>privacy@symptoconnect.com</a></li>
                <li><strong style={{ color: '#191c1e' }}>General Support:</strong> <a href="mailto:support@symptoconnect.com" className="underline" style={{ color: '#00478d' }}>support@symptoconnect.com</a></li>
                <li><strong style={{ color: '#191c1e' }}>Mailing Address:</strong> SymptoConnect, Inc. · Privacy Dept. · 100 Health Way, San Francisco, CA 94105</li>
              </ul>
              <p>We respond to all privacy requests within 30 days.</p>
            </Section>
          </main>
        </div>
      </div>

      {/* Footer */}
      <footer className="py-8" style={{ background: '#191c1e', color: 'rgba(255,255,255,0.5)' }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs">
          <p>© 2026 SymptoConnect. All rights reserved.</p>
          <div className="flex gap-5">
            <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
            <Link to="/contact" className="hover:text-white transition-colors">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPage;
